import {
  DirectionalHint,
  Icon,
  Label,
  mergeStyleSets,
  TooltipHost,
  useTheme,
} from "@fluentui/react";
import { useFormikContext } from "formik";
import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { ClientName } from "./components/ClientName";
import LocationFields from "./components/LocationFields";
import { RiskUsers } from "./components/RiskUsers/RiskUsers";
import { useSelector } from "../../store/hooks";
import { useCurrentRoute } from "../../config/routes";
import DoubleNumberInputField from "../../components/controls/DoubleNumberInputField";
import TextInputField from "../../components/controls/TextInputField";
import { transformNumberValue } from "../../form/util";
import { formatDateString } from "../../utils/utils";
import { IStyledTheme } from "../../theme/types";

const riskUserRoles = [
  "admin",
  "internal",
  "internal_role",
  "external_advanced",
];

const maxNumberValue = 1000000;

const numberFields = [
  "turnoverInMioEur",
  "tsiinMioEur",
  "pmlinMioEur",
  "emlinMioEur",
  "ebitinMioEur",
  "ygpcminMioEur",
  "deductiblesInMioEur",
];

function LocationForm() {
  const userRole = useSelector((s) => s.userRole.userRole);
  const route = useCurrentRoute();
  const isEdit = route === "editLocation";
  const { t } = useTranslation();
  const theme = useTheme() as IStyledTheme;

  const { values, handleChange, errors, setFieldValue } =
    useFormikContext<any>();

  const showRiskUsers = riskUserRoles.includes(userRole);

  useEffect(() => {
    numberFields.forEach((field) => {
      if (values[field]) {
        if (transformNumberValue(values[field].toString()) > maxNumberValue) {
          setFieldValue(field, maxNumberValue.toString());
        }
      }
    });
  }, [values]);

  return (
    <div className={classes.grid}>
      <div className={classes.gridRow}>
        <ClientName name="webBasePartnerNumber" required />
        <LocationFields />
      </div>
      <div className={classes.gridRow}>
        <DoubleNumberInputField
          required
          setFieldValue={setFieldValue}
          value={values?.turnoverInMioEur}
          onChange={handleChange}
          isNegativeAllowed
          errors={errors}
          name={"turnoverInMioEur"}
          disabled={false}
          label={"ram.locationForm.turnoverInMioEUR.label"}
        />
        <DoubleNumberInputField
          required
          setFieldValue={setFieldValue}
          value={values?.tsiinMioEur}
          onChange={handleChange}
          isNegativeAllowed
          errors={errors}
          name={"tsiinMioEur"}
          disabled={false}
          label={"ram.locationForm.TSIInMioEUR.label"}
        />
        <DoubleNumberInputField
          required={false}
          setFieldValue={setFieldValue}
          value={values?.ebitinMioEur}
          onChange={handleChange}
          isNegativeAllowed
          errors={errors}
          name={"ebitinMioEur"}
          disabled={false}
          label={"ram.locationForm.EBITInMioEUR.label"}
        />
        <DoubleNumberInputField
          required={false}
          setFieldValue={setFieldValue}
          value={values?.ygpcminMioEur}
          onChange={handleChange}
          isNegativeAllowed
          errors={errors}
          name={"ygpcminMioEur"}
          disabled={false}
          label={"ram.locationForm.YGPCMInMioEUR.label"}
        />
        <DoubleNumberInputField
          required={false}
          setFieldValue={setFieldValue}
          value={values?.deductiblesInMioEur}
          onChange={handleChange}
          isNegativeAllowed
          errors={errors}
          name={"deductiblesInMioEur"}
          disabled={false}
          label={"ram.locationForm.deductiblesInMioEUR.label"}
        />
        {isEdit && (
          <div
            style={{
              border: `1px solid ${theme.palette.neutralTertiaryAlt}`,
              borderRadius: 4,
              padding: "12px 16px",
              marginBottom: theme.spacing.m,
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                marginBottom: 12,
              }}
            >
              <TooltipHost
                content={t("ram.locationForm.reportData.tooltip")}
                directionalHint={DirectionalHint.rightCenter}
              >
                <Icon
                  iconName="Info"
                  style={{
                    fontSize: 16,
                    color: theme.palette.themePrimary,
                    marginRight: 6,
                    cursor: "help",
                  }}
                />
              </TooltipHost>
              <Label style={{ margin: 0 }}>
                {t("ram.locationForm.reportData.label")}
              </Label>
              <span
                style={{
                  marginLeft: "auto",
                  fontSize: 13,
                  color: theme.palette.neutralSecondary,
                  fontStyle: values?.dataReportId ? "normal" : "italic",
                }}
              >
                {values?.dataReportName
                  ? `${t("ram.locationForm.reportData.source", {
                      reportName: values.dataReportName,
                    })}${values?.dataReportSurveyDate ? ` (${formatDateString(values.dataReportSurveyDate)})` : ""}`
                  : t("ram.locationForm.reportData.noReportLinked")}
              </span>
            </div>
            {values?.reportDataUnsaved && (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  marginBottom: 12,
                  padding: "6px 10px",
                  backgroundColor: theme.palette.themeLighterAlt,
                  borderRadius: 3,
                  fontSize: 13,
                }}
              >
                <Icon
                  iconName="Warning"
                  style={{
                    color: theme.palette.themeDarkAlt,
                    marginRight: 6,
                    fontSize: 14,
                  }}
                />
                <span style={{ color: theme.palette.neutralPrimary }}>
                  {t("ram.locationForm.reportData.unsaved")}
                </span>
              </div>
            )}
            <DoubleNumberInputField
              required={false}
              setFieldValue={setFieldValue}
              value={values?.pmlinMioEur}
              onChange={handleChange}
              isNegativeAllowed
              errors={errors}
              name={"pmlinMioEur"}
              disabled={true}
              label={"ram.locationForm.pmlinMioEur.label"}
            />
            <DoubleNumberInputField
              required={false}
              setFieldValue={setFieldValue}
              value={values?.emlinMioEur}
              onChange={handleChange}
              isNegativeAllowed
              errors={errors}
              name={"emlinMioEur"}
              disabled={true}
              label={"ram.locationForm.EMLInMioEUR.label"}
            />
            <TextInputField
              value={values?.insurersRaiting}
              onChange={handleChange}
              errors={errors}
              name={"insurersRaiting"}
              multiline
              rows={4}
              disabled={true}
              label={"ram.locationForm.insurersRaiting.label"}
            />
          </div>
        )}
        {showRiskUsers && <RiskUsers />}
      </div>
    </div>
  );
}

export default LocationForm;

const classes = mergeStyleSets({
  grid: {
    width: "100%",
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gridGap: "20px",
    overfow: "auto",
  },
  gridRow: {
    width: "100%",
  },
});
